import { acquireAccessToken } from "./access-token-service";
import { AllocationsApiUrl } from "env-config";
import { BadRequestError, ForbiddenError, HttpError, ValidationFailuresResponse } from "components/ServerValidationErrors";

const baseApiUrl = (): string => AllocationsApiUrl().replace(/[/]$/, "");

export type guid = string;
//#region Type definitions

export type QuerySnapshotOptions = {
    partitionId: guid;
};

export type AllocationLinkItem = {
    href: string;
    rel: string;
    method: string;
}

export type QuerySnapshotResponse = {
    totalCount: number;
    items: QuerySnapshotResponseItem[];
    links: AllocationLinkItem[];
}

export type QuerySnapshotResponseItem = {
    productId: number;
    quantity: number;
    lastTransactionId: number;
};

export type QuerySnapshotByProductResponseItem = {
    productId: number;
    quantity: number;
    partitionId: guid;
};

export type QuerySnapshotByProductOptions = {
    productId: number;
    itemsPerPage: number | null;
    PageToken: string | null;
};

export type QuerySnapshotByProductResponse = {
    totalCount: number;
    items: QuerySnapshotByProductResponseItem[];
    links: undefined;
};

export type QueryTransactionsOptions = {
    partitionId: guid;
    startDateTime?: Date;
    endDateTime?: Date;
    itemsPerPage: number | null;
    PageToken: string | null;
};

export type QueryTransactionsResponse = {
    totalCount: number;
    items: QueryTransactionsResponseItem[];
    links: undefined;
};

export type QueryTransactionsResponseItem = {
    productId: number;
    quantityChange: number;
    createdDateTime: string;
    originId: guid | null;
    destinationId: guid | null;
    userId: number;
};

export type ManagedProductResponse = {
    productId: number;
    ledgerManaged: boolean; 
};

export type MultipleManagedProductResponse = {
    productId: number;
    ledgerManaged: boolean | null; 
};

//#endregion

async function apiFetch<TResponse>(path: string, options: RequestInit): Promise<TResponse | null> {
    const url = `${baseApiUrl()}${path}`;
    const accessToken = await acquireAccessToken("api://stock-partition-api", "access");
    const response = await fetch(url, {
        ...options,
        headers: { ...options.headers, Authorization: `Bearer ${accessToken}` },
    });
    if (response.ok) {
        if (response.status === 204) return null;
        return (await response.json()) as TResponse;
    } else if (response.status === 400) {
        throw new BadRequestError(
            response.status,
            response.statusText,
            (await response.json()) as ValidationFailuresResponse
        );
    } else if (response.status === 403) {
        throw new ForbiddenError(response.status, await response.text());
    } else {
        throw new HttpError(response.status, await response.text());
    }
}

function toQuery(obj: any) {
    return Object.keys(obj)
        .filter((k) => obj[k] !== undefined)
        .filter((k) => obj[k] !== null)
        .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(obj[k])}`)
        .join("&");
}

export async function querySnapshot(options: QuerySnapshotOptions): Promise<QuerySnapshotResponse> {
    return (await apiFetch<QuerySnapshotResponse>(`/allocations/snapshots/search?${toQuery(options)}`, {
        headers: { "Content-Type": "application/json" },
    })) ?? {
        totalCount: 0,
        items: [],
        links: []
    };
}

export async function querySnapshotByProduct(options: QuerySnapshotByProductOptions): Promise<QuerySnapshotByProductResponse> {
    return (await apiFetch<QuerySnapshotByProductResponse>(`/allocations/snapshots/products?${toQuery(options)}`, {
        headers: { "Content-Type": "application/json" },
    })) ?? {
        totalCount: 0,
        items: [],
        links: undefined,
    };
}

export async function queryTransactions(options: QueryTransactionsOptions): Promise<QueryTransactionsResponse> {
    return (await apiFetch<QueryTransactionsResponse>(`/allocations/transactions?${toQuery(options)}`, {
        headers: { "Content-Type": "application/json" },
    })) ?? {
        totalCount: 0,
        items: [],
        links: undefined,
    };
}

export async function getProductLedgerManagedStatus(productId: number): Promise<ManagedProductResponse> {
    return (await apiFetch<ManagedProductResponse>(`/managedproducts/${productId}/`, {
        headers: { "Content-Type": "application/json" }
    })) ?? { productId: productId, ledgerManaged: false };
}
export async function getMultipleProductLedgerManagedStatus(productIds: number[]): Promise<MultipleManagedProductResponse[]> {
    const query = productIds.map((id) => `${encodeURIComponent(id)}`).join(", ");
    return (await apiFetch<MultipleManagedProductResponse[]>(`/managedproducts?Ids=${query}`, {
        headers: { "Content-Type": "application/json" }
    })) as MultipleManagedProductResponse[];
}
export type CreateAdjustmentOptions = {
    fieldCompanyId: number,
    partitionId: guid,
    productId: number,
    quantityChange: number,
    notes: string
}

export type CreateMovementOptions = {
    fieldCompanyId: number,
    sourcePartitionId: guid,
    targetPartitionId: guid,
    productId: number,
    quantityChange: number
}

enum TransactionDiscriminator {
    None = 0,
    Adjustment = 1,
    Movement = 2,
}

export type CreateAdjustmentTransactionDto = {
    transactionType: TransactionDiscriminator,
    lastTransactionId?: number,
    fieldCompanyId: number,
    partitionId: guid,
    productId: number,
    quantityChange: number
}

export type CreateMovementTransactionDto = {
    transactionType: TransactionDiscriminator,
    lastTransactionId?: number,
    fieldCompanyId: number,
    sourcePartitionId: guid,
    targetPartitionId: guid,
    productId: number,
    quantityChange: number
}

export async function createAdjustment(dto: CreateAdjustmentOptions): Promise<void> {
    let body: CreateAdjustmentTransactionDto = {
        ...dto,
        transactionType: TransactionDiscriminator.Adjustment
    };

    await apiFetch<any>('/allocations/transactions', {
        headers: { "Content-Type": "application/json" },
        method: 'POST',
        body: JSON.stringify(body)
    });
}

export async function createMovement(dto: CreateMovementOptions): Promise<void> {
    let body: CreateMovementTransactionDto = {
        ...dto,
        transactionType: TransactionDiscriminator.Movement
    };

    await apiFetch<any>('/allocations/transactions', {
        headers: { "Content-Type": "application/json" },
        method: 'POST',
        body: JSON.stringify(body)
    });
}

export type UpdateManagedProductsDto = {
    productIds: number[],
    ledgerManaged: boolean
};

export async function updateManagedProducts(dto: UpdateManagedProductsDto): Promise<void> {
    await apiFetch<any>('/managedproducts/', {
        headers: { "Content-Type": "application/json" },
        method: 'PUT',
        body: JSON.stringify(dto)
    });
}